import {
  ISchema,
  IValidationDescription,
  IsStringDateMetaKey,
  describeSchema,
} from '@framework/validation';
import { EngineInternalFieldSuffix } from '../../schema/expressions/EngineInternalFieldSuffix';
import { IEngineSectionStatus } from '../status/SectionStatusSchema';
import { serializeFormSectionsStatus } from '../status/serializeFormSectionsStatus';

export function serializeFormValues(
  schema: ISchema<any>,
  formValues: Record<string, any>,
) {
  const description = describeSchema(schema, { value: formValues });
  return serializeFormSchema(
    undefined,
    description,
    true,
  ) as ISerializedRootFormValue;
}

export type ISerializedFormValue =
  | ISerializedObjectFormValue
  | ISerializedArrayFormValue
  | ISerializedPrimitiveFormValue
  | ISerializedRootFormValue
  | null;

export interface ISerializedRootFormValue {
  name?: string; // Rimuovere
  version?: string;
  values?: ISerializedFormValue[];
  sections?: Record<string, IEngineSectionStatus>;
}

export interface ISerializedObjectFormValue {
  name?: string;
  type: 'object';
  values: ISerializedFormValue[];
}

export interface ISerializedArrayFormValue {
  name?: string;
  type: 'array';
  items: ISerializedFormValue[];
}

export interface ISerializedPrimitiveFormValue {
  name?: string;
  type: 'string' | 'number' | 'boolean' | 'date';
  value: any;
}

function serializeFormSchema(
  name: string | undefined,
  schema: IValidationDescription,
  isRoot = false,
): ISerializedFormValue {
  switch (schema.type) {
    case 'object': {
      const values = Object.entries(schema.schemas)
        .filter(
          // Ignora i campi interni (finiscono con `@internal`) oppure i campi
          // che non hanno uno schema associato
          ([k, v]) => v !== undefined && !k.endsWith(EngineInternalFieldSuffix),
        )
        .map(([key, value]) => {
          return serializeFormSchema(key, value);
        })
        .filter(v => v != null);

      if (isRoot) {
        return {
          version: 'v1',
          values,
          ...serializeFormSectionsStatus(schema),
        };
      }

      return {
        name,
        type: 'object',
        values,
      };
    }

    case 'array': {
      if (schema.innerType?.type !== 'object') {
        if (schema.value === undefined) return null;

        return {
          name: name ?? '',
          type:
            (schema.innerType?.type as ISerializedPrimitiveFormValue['type']) ??
            'string',
          value: schema.value,
        };
      }

      const values = schema.schemas
        .map((value, index) => {
          return serializeFormSchema('', value);
        })
        .filter(v => v !== undefined);
      return {
        name,
        type: 'array',
        items: values,
      };
    }

    case 'primitive': {
      if (schema.value === undefined) return null;
      return {
        name,
        type: schema.desc?.meta?.[IsStringDateMetaKey]
          ? 'date'
          : (schema.desc?.type as ISerializedPrimitiveFormValue['type']) ??
            'string',
        value: schema.value,
      };
    }
  }
}
